import { createClient } from "@supabase/supabase-js";
import { bearerOf, isTokenlessDataCall, noteTokenlessCall } from "./lib/tokenlessCall";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// "Wifi connected but no internet" (ship/PPU wifi) makes requests HANG for
// minutes instead of failing fast. Auth calls (session refresh at boot) get a
// short deadline so the app can fall back to the saved session; data calls are
// left alone (PDF generation and uploads can legitimately be slow).
//
// John, on a ship 2026-07-12: weak signal = white screen until it gave up, but
// airplane mode loaded instantly (a failed fetch is fast; a hung one is not).
// This deadline is what forces a hung auth refresh to fail fast. It now ALWAYS
// applies: older iPads lack AbortSignal.timeout, so fall back to a manual
// AbortController rather than leaving the socket to hang.
const AUTH_TIMEOUT_MS = 6000;
const hasAbortTimeout =
  typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function";

// fetch() with a hard deadline that actually aborts the request, with a
// manual-AbortController fallback for browsers without AbortSignal.timeout.
function fetchWithDeadline(input, init, ms) {
  if (hasAbortTimeout) return fetch(input, { ...init, signal: AbortSignal.timeout(ms) });
  if (typeof AbortController === "undefined") return fetch(input, init);
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  return fetch(input, { ...init, signal: ctrl.signal }).finally(() => clearTimeout(t));
}

// --- Token-refresh circuit breaker -----------------------------------------
// No single client should EVER fire the token-refresh endpoint in a tight
// loop. On 2026-07-18 (Jill's iPad, after a travel day of online/offline
// flapping) several uncoordinated refreshers — the built-in autoRefresh, the
// reconnect probe's refreshSession, and setSession re-feeding a stale backup
// token — raced on Supabase's SINGLE-USE (rotated) refresh token. Each loser
// got 400 "Refresh Token Not Found", the client wiped its session, we re-fed
// the same dead backup token, and it ran at ~11 req/s: 100 calls to /token in
// 9 seconds tripped Supabase's rate limit and HARD-LOCKED her out.
//
// This guard sits at the fetch layer so it catches the storm regardless of
// which refresher triggers it: cap /token calls to TOKEN_MAX per rolling
// window; once exceeded, trip a cooldown during which /token calls fail fast
// LOCALLY (a synthetic network error) instead of ever reaching the server.
// supabase-js treats that as a transient network failure and backs off — the
// app keeps running on its current tokens, and AuthContext raises the
// sign-in-to-sync banner. The endpoint is never hammered, so the rate limit
// is never tripped.
const TOKEN_MAX = 6; // allowed /token calls...
const TOKEN_WINDOW_MS = 10000; // ...per 10s before the breaker trips
const TOKEN_COOLDOWN_MS = 30000; // once tripped, stay open this long
let tokenHits = [];
let tokenTrippedAt = 0;

export function tokenCircuitTripped() {
  if (!tokenTrippedAt) return false;
  if (Date.now() - tokenTrippedAt < TOKEN_COOLDOWN_MS) return true;
  tokenTrippedAt = 0;
  tokenHits = [];
  return false;
}

// Returns true if this /token call must be blocked (breaker already open, or
// this call would exceed the window). Records the call otherwise.
function tokenCircuitBlocks() {
  const now = Date.now();
  if (tokenTrippedAt) {
    if (now - tokenTrippedAt < TOKEN_COOLDOWN_MS) return true;
    tokenTrippedAt = 0; // cooldown elapsed — start fresh
    tokenHits = [];
  }
  tokenHits = tokenHits.filter((t) => now - t < TOKEN_WINDOW_MS);
  if (tokenHits.length >= TOKEN_MAX) {
    tokenTrippedAt = now;
    console.warn(
      "supabaseClient: token-refresh circuit tripped — too many refreshes in a short window; backing off for",
      TOKEN_COOLDOWN_MS / 1000,
      "s"
    );
    return true;
  }
  tokenHits.push(now);
  return false;
}

const authTimeoutFetch = (input, init = {}) => {
  const url = typeof input === "string" ? input : input?.url || "";
  const method = (init.method || (typeof input === "object" && input?.method) || "GET").toUpperCase();
  // Only the refresh/grant endpoint is guarded — never health checks or sign-in.
  if (url.includes("/auth/v1/token") && method === "POST" && tokenCircuitBlocks()) {
    // Message intentionally reads as a network/fetch failure: callers that
    // classify errors (AuthContext's setSession handler) then treat a tripped
    // breaker as a TRANSIENT blip — keep the session, retry later — never as a
    // server revocation that would wipe the saved tokens.
    return Promise.reject(
      new TypeError("network fetch aborted: token-refresh circuit open (client-side rate guard)")
    );
  }
  if (url.includes("/auth/v1/") && !init.signal) {
    return fetchWithDeadline(input, init, AUTH_TIMEOUT_MS);
  }
  // A data call going out with the public key instead of a login token can
  // only be refused as anonymous; tell AuthContext so the sign-in banner
  // shows instead of every page failing quietly (see lib/tokenlessCall.js).
  if (isTokenlessDataCall(url, bearerOf(input, init), supabaseAnonKey)) noteTokenlessCall(url);
  return fetch(input, init);
};

// Signed out on purpose (seapa_signed_out, see lib/offlineIdentity.js): a login
// token still on this device at launch is a leftover, e.g. the app was closed
// in the middle of an offline sign-out. Drop it BEFORE the client reads it, or
// supabase-js announces it as SIGNED_IN and the pilot is quietly signed back
// in (Codex review of #506). A real new sign-in or a reset link writes a
// fresh token after this and clears the flag.
try {
  if (localStorage.getItem("seapa_signed_out") === "1") {
    localStorage.removeItem(`sb-${new URL(supabaseUrl).hostname.split(".")[0]}-auth-token`);
  }
} catch { /* storage unavailable */ }

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    storage: localStorage,
    autoRefreshToken: true,
  },
  global: {
    fetch: authTimeoutFetch,
  },
});

// Read the session Supabase persisted in localStorage WITHOUT any network I/O.
// Offline-boot fallback: the access token may be expired, which is fine for
// rendering cached data; Supabase refreshes it in the background once the
// network is really back.
export function readCachedSession() {
  try {
    const ref = new URL(supabaseUrl).hostname.split(".")[0];
    const raw = localStorage.getItem(`sb-${ref}-auth-token`);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.user) return parsed;
    }
    // Supabase clears its own token when a refresh fails — the backup mirror
    // (written by AuthContext on every sign-in/refresh) survives that, so
    // dead-wifi boots still have a session to run on.
    const backup = localStorage.getItem("seapa_session_backup");
    if (backup) {
      const parsed = JSON.parse(backup);
      if (parsed?.user) return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

console.log("✅ Supabase initialized with:", supabaseUrl);
// Remove Supabase's own stored login from this device without any network
// I/O, and return the access token it held. Sign Out does this FIRST: offline
// or on dead ship wifi, supabase.auth.signOut() fails its server call and
// returns BEFORE clearing local storage, so the token stayed and the next
// launch booted straight back in (found 2026-09-24).
export function clearStoredAuthToken() {
  let accessToken = null;
  try {
    const ref = new URL(supabaseUrl).hostname.split(".")[0];
    try { accessToken = JSON.parse(localStorage.getItem(`sb-${ref}-auth-token`) || "null")?.access_token || null; } catch { /* unreadable */ }
    localStorage.removeItem(`sb-${ref}-auth-token`);
    localStorage.removeItem(`sb-${ref}-auth-token-code-verifier`);
  } catch { /* ignore */ }
  return accessToken;
}

// Best-effort: tell the server to end this login everywhere (what
// supabase.auth.signOut() does by default). Never awaited by Sign Out, so no
// connection means no wait; the device copy is already gone.
export function revokeLoginOnServer(accessToken) {
  if (!accessToken) return;
  try {
    fetch(`${supabaseUrl}/auth/v1/logout?scope=global`, {
      method: "POST",
      headers: { apikey: supabaseAnonKey, Authorization: `Bearer ${accessToken}` },
      ...(typeof AbortSignal !== "undefined" && AbortSignal.timeout ? { signal: AbortSignal.timeout(8000) } : {}),
    }).catch(() => { /* offline: the device copy is already gone */ });
  } catch { /* ignore */ }
}
